import logging from 'utils/logging';
import { GAMESTATE_LOBBY } from '../../gamestate/constants';
import { HOST_STATUS, MOMENT_TYPES } from '../hostStateSlice';
import { registerCacheReset } from '../momentCacheManager';

const log = logging.getLogger('moments.PlayerJoined');
log.setLevel('info');

// Track which players have been welcomed
const welcomedPlayers = new Set();

export const resetPlayerJoinedCache = () => {
  welcomedPlayers.clear();
};
registerCacheReset('all', resetPlayerJoinedCache);

/**
 * Check if a player has already been welcomed
 * @param {string} playerId - The player ID to check
 * @returns {boolean} - Whether the player has already been welcomed
 */
const isPlayerWelcomed = (playerId) => {
  return welcomedPlayers.has(playerId);
};

/**
 * Mark a player as welcomed
 * @param {string} playerId - The player ID to mark as welcomed
 */
const markPlayerWelcomed = (playerId) => {
  welcomedPlayers.add(playerId);
  log.debug(`Marked player ${playerId} as welcomed`);
};

/**
 * Moment that triggers when a new player joins in the lobby
 */
export const PlayerJoinedMoment = {
  id: 'PLAYER_JOINED',
  /**
   * Detects when a new player has joined the game while in lobby
   * @param {Object} prevState - Previous game state
   * @param {Object} currentState - Current game state
   * @param {Array} currentPlayers - Current players array
   * @param {Array} prevPlayers - Previous players array
   * @returns {Object|null} - Player that joined or null if no new player
   */
  shouldTrigger: ({ prevState, currentState, currentPlayers, prevPlayers = [] }) => {
    // Only trigger in initial lobby (no round), not inter-round lobby
    if (currentState?.currentState !== GAMESTATE_LOBBY) {
      return false;
    }
    if (currentState?.round) {
      return false;
    }
    
    // Need both player lists to compare
    if (!currentPlayers || !prevPlayers) {
      return false;
    }
    
    log.debug('Checking for newly joined players', {
      prevPlayerCount: prevPlayers.length,
      currentPlayerCount: currentPlayers.length
    });
    
    // Find players that are in currentPlayers but not in prevPlayers
    const prevPlayerIds = new Set(prevPlayers.map(p => p.id));
    const newPlayers = currentPlayers.filter(p => !prevPlayerIds.has(p.id));
    
    // Return the first new player found (if any)
    if (newPlayers.length > 0) {
      const newPlayer = newPlayers[0];
      log.info(`New player joined: ${newPlayer.name} (${newPlayer.id})`);
      
      if (!newPlayer) {
        log.error("No new player found in PlayerJoinedMoment.shouldTrigger");
        return false;
      }
      
      const narrationKey = `PLAYER_WELCOME_${newPlayer.id}`;
      
      return {
        shouldTriggerAction: false,
        shouldGenerateNarration: true,
        narrationParams: {
          narrationKey,
          momentType: MOMENT_TYPES.PLAYER_JOINED,
          context: {
            // Store player ID as a custom field in the narration params
            playerId: newPlayer.id,
            playerName: newPlayer.name,
            newPlayer,
            playerCount: currentPlayers.length,
            otherPlayers: currentPlayers.filter(p => p.id !== newPlayer.id),
            isFirstPlayer: currentPlayers.length === 1
          }
        }
      };
    }
    
    return false;
  }
};

/**
 * Playback moment for player welcome narrations
 */
export const PlayerJoinedPlaybackMoment = {
  id: 'PLAY_PLAYER_WELCOME',
  narrationKey: 'PLAYER_WELCOME',
  momentType: MOMENT_TYPES.PLAYER_JOINED,
  // narrationKey is dynamic, will be checked by pattern
  shouldPlay: (gameState, hostState, narration, playedNarrations) => {
    // Check if narration exists
    if (!narration) {
      return false;
    }

    log.debug('PlayerJoinedPlaybackMoment shouldPlay', { gameState, hostState, narration, playedNarrations });
    
    // Extract the player ID directly from the narration object
    const playerId = narration.context?.playerId;
    
    // Check if this is a player welcome narration
    if (!narration.key || !narration.key.startsWith('PLAYER_WELCOME_')) {
      return false;
    }
    
    // Check if this player has already been welcomed
    if (playerId && isPlayerWelcomed(playerId)) {
      log.debug(`Player ${playerId} has already been welcomed`);
      return false;
    }
    
    // Only play in initial lobby (no round), not inter-round lobby
    const inLobby = gameState?.currentState === GAMESTATE_LOBBY && !gameState?.round;
    
    // Check if narration exists and is ready to play
    const narrationReady = narration.textStatus === "READY" && (narration.audioStatus === "READY" || narration.audioStatus === "ERROR");
    
    // Check if host is available to speak (not already speaking)
    const hostAvailable = hostState?.status !== HOST_STATUS.SPEAKING;
    
    // Check if this specific narration has been played before in the playedNarrations set
    // This is an additional check beyond our welcomedPlayers tracking
    const notPlayedYet = !playedNarrations.has(narration.key);
    
    log.debug(`PLAY_PLAYER_WELCOME check: inLobby=${inLobby}, narrationReady=${narrationReady}, hostAvailable=${hostAvailable}, notPlayedYet=${notPlayedYet}`);
    
    return inLobby && narrationReady && hostAvailable && notPlayedYet;
  },
  onComplete: (gameId, dispatch, getState) => {
    // Get the narration that was just played
    const state = getState();
    const currentNarrationKey = state.game?.game?.hostState?.currentNarrationKey;
    
    if (!currentNarrationKey) return;
    
    // Get the complete narration object
    const narration = state.game?.game?.hostState?.narration?.[currentNarrationKey];
    
    if (narration && narration.playerId) {
      // Use the direct playerId from the narration object
      markPlayerWelcomed(narration.playerId);
    }
  }
};

const api = {
  trigger: PlayerJoinedMoment,
  playback: PlayerJoinedPlaybackMoment
};

export default api;
